import { useCallback, useEffect, useState } from "react";
import { VoucherStatus } from "../types/types";
import { getRewardStatus } from "../../../../../clients/inbox-rewards";
import { getAvatarId, useJwt } from "../../../../../utils/jwt";
import { CachedVoucherStatuses } from "../types/schemas";

export function useVoucherStatus({ voucher }: { voucher: string | null }) {
    const [status, setStatus] = useState<VoucherStatus | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const jwt = useJwt();

    const checkStatus = useCallback(async () => {
        if (!jwt || !voucher) return;

        const avatarId = getAvatarId(jwt ?? "") ?? "";

        try {
            setLoading(true);
            setError(false);

            const data = await getRewardStatus([voucher], jwt);
            const voucherStatus = data?.[voucher]?.status ?? null;

            console.log("Checked voucher status:", data);
            if (voucherStatus !== null) {
                setStatus(voucherStatus);
                setError(false);

                if (voucherStatus === "VOUCHER_REDEEMED") {
                    const cachedVoucherStatuses = localStorage.getItem(
                        `velvet-glitch-voucher-statuses-${avatarId}`,
                    );
                    const updatedVoucherStatuses = cachedVoucherStatuses
                        ? JSON.parse(cachedVoucherStatuses)
                        : {};
                    updatedVoucherStatuses[voucher] = voucherStatus;
                    localStorage.setItem(
                        `velvet-glitch-voucher-statuses-${avatarId}`,
                        JSON.stringify(updatedVoucherStatuses),
                    );
                }
            }
        } catch (e) {
            console.error("Error fetching voucher status", e);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [voucher, jwt]);

    useEffect(() => {
        if (!jwt) return;
        if (!voucher) return;

        const avatarId = getAvatarId(jwt ?? "") ?? "";

        const cachedVoucherStatuses = localStorage.getItem(
            `velvet-glitch-voucher-statuses-${avatarId}`,
        );

        if (cachedVoucherStatuses) {
            try {
                const parsedCache = JSON.parse(cachedVoucherStatuses);
                const validationResult =
                    CachedVoucherStatuses.safeParse(parsedCache);

                if (validationResult.success) {
                    const vouchers = validationResult.data;
                    const cachedVoucherStatus = vouchers[voucher];

                    if (cachedVoucherStatus) {
                        console.log(
                            "Using cached voucher status for voucher:",
                            voucher,
                        );
                        setStatus(cachedVoucherStatus);
                        setLoading(false);
                        setError(false);
                        return;
                    } else {
                        console.log(
                            "No cached voucher status found for voucher:",
                            voucher,
                        );
                    }
                } else {
                    console.error(
                        "Failed to validate cached voucher statuses:",
                        validationResult.error,
                    );

                    localStorage.removeItem(
                        `velvet-glitch-voucher-statuses-${avatarId}`,
                    );
                }
            } catch (e) {
                console.error("Failed to parse cached voucher statuses:", e);
            }
        }

        checkStatus();
    }, [voucher, jwt, checkStatus]);

    return {
        status,
        loading,
        error,
        checkStatus,
    };
}
